import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { Reveal } from "@/components/motion";
import { frameworks, frameworkDisclaimer } from "@/data/frameworks";

const ORDER = ["BRSR", "BRSR Core", "GRI", "ISSB", "IFRS S1", "IFRS S2", "GHG Protocol", "TCFD", "TNFD", "SFDR", "UN PRI", "UN SDGs"];
const shown = ORDER.map((code) => frameworks.find((f) => f.code === code)).filter(Boolean);

export default function Frameworks() {
  const [open, setOpen] = useState(null);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <section className="bg-white text-ink sec" data-testid="frameworks-section">
      <div className="wrap">
        <Reveal>
          <h2 className="h-display font-bold text-[32px] sm:text-5xl lg:text-[56px] leading-[1.06] mb-14">
            Frameworks we work with.
          </h2>
        </Reveal>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 border-t border-l rule-light" data-testid="frameworks-grid">
          {shown.map((f, i) => (
            <Reveal key={f.code} delay={(i % 4) * 0.05}>
              <button
                onClick={() => setOpen(f)}
                className="w-full h-full border-b border-r rule-light py-8 sm:py-10 px-4 text-center h-display font-bold text-[17px] sm:text-xl text-ink hover:text-gold hover:bg-ink/[0.02] transition-colors duration-300"
                data-testid={`framework-item-${f.code.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {f.code}
              </button>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="text-[11.5px] text-ink/45 mt-7 max-w-3xl leading-relaxed" data-testid="framework-disclaimer">
            {frameworkDisclaimer}
          </p>
        </Reveal>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-ink/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
            onClick={() => setOpen(null)}
            data-testid="framework-modal-overlay"
          >
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white text-ink w-full sm:max-w-xl max-h-[86vh] overflow-y-auto p-8 sm:p-11"
              data-testid="framework-modal"
            >
              <div className="flex items-start justify-between gap-6">
                <h3 className="h-display font-bold text-3xl">{open.code}</h3>
                <button onClick={() => setOpen(null)} className="p-2 text-ink/50 hover:text-ink" aria-label="Close" data-testid="framework-modal-close">
                  <X size={20} />
                </button>
              </div>
              <div className="mt-7 space-y-6">
                {[
                  ["What it is", open.what],
                  ["Who it applies to", open.who],
                  ["Why it matters", open.why],
                  ["How SarvaRitam can help", open.help],
                ].map(([k, v]) => (
                  <div key={k}>
                    <p className="eyebrow text-ink/50 mb-2">{k}</p>
                    <p className="text-[14.5px] text-ink/75 leading-relaxed">{v}</p>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-ink/40 mt-8 pt-5 border-t rule-light leading-relaxed">{frameworkDisclaimer}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
